/** Must match Sp\Log\Event\ScreenshotAttempt::SCOPES on the server. */
export type ScreenshotAttemptScope =
    | 'intro'
    | 'home'
    | 'all_photos'
    | 'album'
    | 'photo'
    | 'favorites'
    | 'label'
    | 'compare'
    | 'info'
    | 'other';

export interface IScreenshotAttemptContext {
    scope: ScreenshotAttemptScope;
    albumId: number | null;
    photoId: number | null;
}

const toId = (value: string | undefined): number | null => {
    const id = Number(value);

    return Number.isInteger(id) && id > 0 ? id : null;
};

const getScope = (
    originalPath: string,
    params: Record<string, string | undefined>
): ScreenshotAttemptScope => {
    // Any route ending in a photo (home, album, favorites, label, hidden, search) is the photo view.
    if (originalPath.endsWith('/photo/:photoId')) {
        return 'photo';
    }

    if (originalPath === '/' || originalPath === '/admin') {
        return 'intro';
    }

    // Album cards when the gallery has albums, otherwise every photo.
    if (originalPath === '/home') {
        return 'home';
    }

    if (originalPath === '/album/:albumId') {
        // The All Media tab is the "all" pseudo-album.
        return params.albumId === 'all' ? 'all_photos' : 'album';
    }

    if (originalPath === '/favorites') {
        return 'favorites';
    }

    if (originalPath === '/labels' || originalPath === '/label/:tagName') {
        return 'label';
    }

    if (originalPath === '/compare') {
        return 'compare';
    }

    if (originalPath.startsWith('/info')) {
        return 'info';
    }

    return 'other';
};

/**
 * What the visitor was looking at, from the gallery's current Angular route
 * (`$route.current.$$route.originalPath` and `$route.current.params`).
 */
export const getScreenshotAttemptContext = (
    originalPath: string | undefined,
    params: Record<string, string | undefined> = {}
): IScreenshotAttemptContext => {
    if (originalPath === undefined) {
        return { scope: 'other', albumId: null, photoId: null };
    }

    const scope = getScope(originalPath, params);

    return {
        scope,
        albumId: scope === 'album' || scope === 'photo' ? toId(params.albumId) : null,
        photoId: scope === 'photo' ? toId(params.photoId) : null
    };
};
